/**
 * Map Databricks / sidecar quarantine table rows into the frontend DqxValidationResult
 * shape consumed by DataQualityPage, DqxTable, and dqxExport.
 */

import type {
  DqxCellError,
  DqxErrorCategory,
  DqxErrorReportRow,
  DqxValidationResult,
  KnownMapping,
  SchemaField,
} from "@/types";
import {
  lookupContractRuleMeta,
  type DqxContractRuleMeta,
} from "@/services/dqxContractRulesLookup";
import { buildCrossFieldDetail } from "@/services/dqxCrossFieldDiagnostics";
import { mapContractRuleTypeToBucket } from "@/services/dqxSummaryMetrics";

const PLACEHOLDER_CATEGORY: DqxErrorCategory = "Data Formatting";

const EMPTY_SUMMARY: DqxValidationResult["summary"] = {
  "Data Formatting": 0,
  "Summation Logic": 0,
  "Structure & Syntax": 0,
};

/** One element from quarantine `errors` / `warnings` arrays (DQX / ODCS). */
interface QuarantineRuleRow {
  name?: string;
  message?: string;
  columns?: string[];
  function?: string;
  user_metadata?: Record<string, unknown>;
}

/** Exported for row-level summary metrics (same parsing as validation mapping). */
export function parseQuarantineRulesArray(raw: unknown): QuarantineRuleRow[] {
  if (Array.isArray(raw)) {
    return raw.filter(Boolean) as QuarantineRuleRow[];
  }
  if (typeof raw === "string") {
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as QuarantineRuleRow[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function worksheetRowIndexFromRowId(rowId: unknown, rowCount: number): number {
  const n = typeof rowId === "number" ? rowId : Number(rowId);
  if (!Number.isFinite(n)) return -1;
  const i = Math.trunc(n);
  if (i >= 0 && i < rowCount) return i;
  if (i >= 1 && i <= rowCount) return i - 1;
  return -1;
}

/** SQL / JSON may use different key casings for the logical row index. */
function getQuarantineRowId(row: Record<string, unknown>): unknown {
  const keys = [
    "row_id",
    "rowId",
    "source_row_index",
    "sourceRowIndex",
    "ROW_ID",
    "SOURCE_ROW_INDEX",
  ];
  for (const k of keys) {
    if (k in row) return row[k];
  }
  return undefined;
}

/**
 * Map a quarantine row to a worksheet row index. If `row_id` does not match the
 * parsed sheet (common when IDs are global or 1-based elsewhere), fall back to
 * the row's position in `quarantine_table` when it is within bounds.
 */
/** Same row index resolution as `quarantineRowsToDqxValidationResult` (for summary counts). */
export function resolveQuarantineWorksheetRowIndex(
  row: Record<string, unknown>,
  rowCount: number,
  quarantineTableIndex: number
): number {
  const fromId = worksheetRowIndexFromRowId(getQuarantineRowId(row), rowCount);
  if (fromId >= 0) return fromId;
  if (rowCount > 0 && quarantineTableIndex >= 0 && quarantineTableIndex < rowCount) {
    return quarantineTableIndex;
  }
  // Parsed worksheet empty: still surface quarantine rows by table order (grid may be empty).
  if (rowCount === 0 && quarantineTableIndex >= 0) {
    return quarantineTableIndex;
  }
  return -1;
}

/** Read a field from a quarantine row (SQL/JSON key casing varies). */
export function getQuarantineRowField(row: Record<string, unknown>, ...names: string[]): unknown {
  for (const n of names) {
    if (n in row) return row[n];
  }
  const lower = names.map((n) => n.toLowerCase());
  for (const k of Object.keys(row)) {
    const kl = k.toLowerCase();
    const idx = lower.indexOf(kl);
    if (idx !== -1) return row[k];
  }
  return undefined;
}

export function parseInputData(raw: unknown): Record<string, unknown> | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === "string") {
    try {
      const p: unknown = JSON.parse(raw);
      if (typeof p === "object" && p !== null && !Array.isArray(p))
        return p as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Errors-only source grid synthesised from quarantine `input_data`, used when
 * the page is opened without an upload session. Non-failing rows aren't on
 * the wire, so gaps are padded with empty rows to keep cellErrors keys aligned.
 */
export function deriveSourceGridFromQuarantine(quarantineTable: Record<string, unknown>[]): {
  headers: string[];
  rows: Record<string, unknown>[];
  rowIndices: number[];
} {
  const headerSet = new Set<string>();
  const rowsByIndex = new Map<number, Record<string, unknown>>();

  for (let i = 0; i < quarantineTable.length; i++) {
    const qrow = quarantineTable[i]!;
    const rawIdx = getQuarantineRowField(
      qrow,
      "row_id",
      "rowId",
      "source_row_index",
      "sourceRowIndex",
      "ROW_ID",
      "SOURCE_ROW_INDEX"
    );
    const n = typeof rawIdx === "number" ? rawIdx : Number(rawIdx);
    const idx = Number.isFinite(n) && n >= 0 ? Math.trunc(n) : i;

    const inputData = parseInputData(
      getQuarantineRowField(qrow, "input_data", "INPUT_DATA", "inputData")
    );
    if (!inputData) continue;

    rowsByIndex.set(idx, inputData);
    for (const k of Object.keys(inputData)) headerSet.add(k);
  }

  if (rowsByIndex.size === 0) return { headers: [], rows: [], rowIndices: [] };

  const sortedIndices = Array.from(rowsByIndex.keys()).sort((a, b) => a - b);
  const maxIdx = sortedIndices[sortedIndices.length - 1] ?? 0;

  const rows: Record<string, unknown>[] = [];
  for (let i = 0; i <= maxIdx; i++) {
    rows.push(rowsByIndex.get(i) ?? {});
  }

  return {
    headers: Array.from(headerSet).sort(),
    rows,
    rowIndices: sortedIndices,
  };
}

function readObjectFieldCaseInsensitive(
  row: Record<string, unknown>,
  key: string
): unknown | undefined {
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const keyLower = key.toLowerCase();
  for (const k of Object.keys(row)) {
    if (k.toLowerCase() === keyLower) return row[k];
  }
  return undefined;
}

/**
 * Quarantine `input_data` may be raw mapped_data JSON keyed by schema field IDs.
 * Mirror those values onto schema field names so cross-field diagnostics can
 * resolve contract names (e.g. Total_Indemnity_Spend) consistently.
 */
function aliasInputDataFieldIdsToFieldNames(
  input: Record<string, unknown> | null,
  schemaFields?: SchemaField[]
): Record<string, unknown> | null {
  if (!input || !schemaFields?.length) return input;

  const out: Record<string, unknown> = { ...input };
  for (const sf of schemaFields) {
    const id = sf.id?.trim();
    const fieldName = sf.fieldName?.trim();
    if (!id || !fieldName) continue;
    if (Object.prototype.hasOwnProperty.call(out, fieldName)) continue;

    const value = readObjectFieldCaseInsensitive(input, id);
    if (value !== undefined) out[fieldName] = value;
  }
  return out;
}

function buildMappingLookups(savedMappings: KnownMapping[]) {
  const sourceToTarget = new Map<string, string>();
  const targetToSource = new Map<string, string>();
  /** Grid headers are often `KnownMapping.targetField` while the contract maps logical names → schema `id`. */
  const targetFieldIdToHeader = new Map<string, string>();
  for (const m of savedMappings) {
    sourceToTarget.set(m.sourceField, m.targetField);
    targetToSource.set(m.targetField, m.sourceField);
    const tid = m.targetFieldId?.trim();
    if (tid) {
      targetFieldIdToHeader.set(tid, m.targetField);
      targetFieldIdToHeader.set(tid.toLowerCase(), m.targetField);
    }
  }
  return { sourceToTarget, targetToSource, targetFieldIdToHeader };
}

/** Normalize contract / API field labels for loose matching (spaces vs underscores, case). */
function normalizeFieldKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
}

/** Map contract / logical field names (DQX `columns[]`) to target field IDs used as grid headers. */
function buildContractFieldNameToTargetId(schemaFields: SchemaField[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const sf of schemaFields) {
    const id = sf.id.trim();
    if (!id) continue;
    m.set(normalizeFieldKey(id), id);
    const fn = sf.fieldName?.trim();
    if (fn) {
      m.set(normalizeFieldKey(fn), id);
      m.set(fn.toLowerCase(), id);
    }
  }
  return m;
}

/**
 * Resolve a column name from quarantine `columns[]` to an index in `headers`.
 * Tries exact header, contract field name → schema field id (when `schemaFields` provided),
 * then source / target mapping lookups for source-column grids.
 */
function headerIndexForLogicalName(
  headerCandidate: string,
  headers: string[],
  normalizedTrimmed: string
): number {
  const c = headerCandidate.trim();
  let idx = headers.indexOf(c);
  if (idx !== -1) return idx;
  idx = headers.findIndex((h) => h.trim().toLowerCase() === c.toLowerCase());
  if (idx !== -1) return idx;
  idx = headers.findIndex((h) => normalizeFieldKey(h) === normalizeFieldKey(c));
  if (idx !== -1) return idx;
  idx = headers.findIndex((h) => normalizeFieldKey(h) === normalizedTrimmed);
  return idx;
}

function resolveColumnIndex(
  columnName: string,
  headers: string[],
  lookups: ReturnType<typeof buildMappingLookups>,
  contractNameToTargetId?: Map<string, string>,
  schemaFields?: SchemaField[]
): number {
  const trimmed = columnName.trim();
  let idx = headers.indexOf(trimmed);
  if (idx !== -1) return idx;

  idx = headers.findIndex((h) => h.trim() === trimmed);
  if (idx !== -1) return idx;

  // Fallback: match quarantine column names directly against rendered headers
  // using case-insensitive and normalized forms (e.g. spaces/hyphens/underscores).
  const normalizedTrimmed = normalizeFieldKey(trimmed);
  idx = headers.findIndex((h) => h.trim().toLowerCase() === trimmed.toLowerCase());
  if (idx !== -1) return idx;
  idx = headers.findIndex((h) => normalizeFieldKey(h) === normalizedTrimmed);
  if (idx !== -1) return idx;

  if (contractNameToTargetId) {
    const targetId =
      contractNameToTargetId.get(normalizeFieldKey(trimmed)) ??
      contractNameToTargetId.get(trimmed.toLowerCase());
    if (targetId) {
      idx = headers.indexOf(targetId);
      if (idx !== -1) return idx;
      idx = headers.findIndex((h) => h.trim().toLowerCase() === targetId.toLowerCase());
      if (idx !== -1) return idx;

      const viaMapping =
        lookups.targetFieldIdToHeader.get(targetId) ??
        lookups.targetFieldIdToHeader.get(targetId.toLowerCase());
      if (viaMapping) {
        idx = headerIndexForLogicalName(viaMapping, headers, normalizedTrimmed);
        if (idx !== -1) return idx;
      }

      if (schemaFields?.length) {
        const sf = schemaFields.find(
          (f) => f.id.trim() === targetId || f.id.trim().toLowerCase() === targetId.toLowerCase()
        );
        const fn = sf?.fieldName?.trim();
        if (fn) {
          idx = headerIndexForLogicalName(fn, headers, normalizedTrimmed);
          if (idx !== -1) return idx;
        }
      }
    }
  }

  const byTarget = lookups.targetToSource.get(trimmed);
  if (byTarget !== undefined) {
    idx = headers.indexOf(byTarget);
    if (idx !== -1) return idx;
  }

  return -1;
}

function metadataFieldName(rule: QuarantineRuleRow): string | undefined {
  const raw = rule.user_metadata?.field;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return undefined;
}

/** When DQX omits `columns[]`, infer logical column names from common message text. */
function extractColumnNamesFromDqxMessage(message: string | undefined): string[] {
  if (!message || typeof message !== "string") return [];
  const seen = new Set<string>();
  const add = (s: string | undefined) => {
    const t = s?.trim();
    if (t) seen.add(t);
  };
  const reColumn = /\bcolumn\s+['"]([^'"]+)['"]/gi;
  let m: RegExpExecArray | null;
  while ((m = reColumn.exec(message)) !== null) {
    add(m[1]);
  }
  const reField = /\bfield\s+['"]([^'"]+)['"]/gi;
  while ((m = reField.exec(message)) !== null) {
    add(m[1]);
  }
  return Array.from(seen);
}

function columnNamesForRule(rule: QuarantineRuleRow): string[] {
  const cols = Array.isArray(rule.columns) ? rule.columns.map(String) : [];
  if (cols.length > 0) return cols;
  const field = metadataFieldName(rule);
  if (field) return [field];
  return extractColumnNamesFromDqxMessage(
    typeof rule.message === "string" ? rule.message : undefined
  );
}

function normalizeRangeDisplayMessage(message: string): string {
  // Keep frontend display aligned with the requested inclusive upper-bound wording.
  return message.replace(
    /(\bmust\s+be\s*<\s*)([-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?)/gi,
    "must be <= $2"
  );
}

function ruleLabel(rule: QuarantineRuleRow): string {
  const msg = typeof rule.message === "string" ? rule.message.trim() : "";
  if (msg) return normalizeRangeDisplayMessage(msg);
  const name = typeof rule.name === "string" ? rule.name.trim() : "";
  if (name) return name;
  return "Validation rule failed";
}

function inListDetailFromMeta(meta: DqxContractRuleMeta | undefined): string | undefined {
  const values = meta?.allowed_values ?? [];
  if (values.length === 0) return undefined;
  const shown = values.slice(0, 12);
  const suffix =
    values.length > shown.length ? `, ... (+${values.length - shown.length} more)` : "";
  return `Allowed values: ${shown.join(", ")}${suffix}`;
}

function ruleName(rule: QuarantineRuleRow): string {
  const name = typeof rule.name === "string" ? rule.name.trim() : "";
  return name;
}

function normalizeRuleTypeKey(s: string | undefined): string {
  return (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/** True when this rule should highlight affected cells in yellow (multi-column / cross-field). */
export function ruleIsMultiCellViolation(
  rule: QuarantineRuleRow,
  meta: DqxContractRuleMeta | undefined,
  resolvedColumnCount: number
): boolean {
  if (resolvedColumnCount > 1) return true;
  const rawCols = Array.isArray(rule.columns) ? rule.columns.map(String) : [];
  if (rawCols.length > 1) return true;
  const rt = normalizeRuleTypeKey(meta?.rule_type);
  if (rt === "summation") return true;
  if (meta?.check_function === "is_unique") {
    const ac = meta.affected_columns;
    if (ac && ac.length > 1) return true;
  }
  if (rt === "conditional" && meta?.affected_columns && meta.affected_columns.length > 1) {
    return true;
  }
  return false;
}

function cellValue(
  rows: Record<string, unknown>[],
  rowIndex: number,
  sourceColumn: string,
  inputFallback: Record<string, unknown> | null
): string {
  const row = rows[rowIndex];
  const src = sourceColumn.trim();
  if (row) {
    if (Object.prototype.hasOwnProperty.call(row, sourceColumn)) {
      return String(row[sourceColumn] ?? "");
    }
    for (const k of Object.keys(row)) {
      if (k.toLowerCase() === src.toLowerCase()) return String(row[k] ?? "");
    }
  }
  if (inputFallback) {
    if (Object.prototype.hasOwnProperty.call(inputFallback, sourceColumn)) {
      return String(inputFallback[sourceColumn] ?? "");
    }
    for (const k of Object.keys(inputFallback)) {
      if (k.toLowerCase() === src.toLowerCase()) return String(inputFallback[k] ?? "");
    }
  }
  return "";
}

/**
 * Convert quarantine detail rows + the worksheet shown on Data Quality into
 * `DqxValidationResult`.
 *
 * - Category breakdown is not inferred; `summary` stays zeroed (see plan).
 * - `DqxCellError.category` uses a placeholder until backend exposes categories.
 */
export function quarantineRowsToDqxValidationResult(
  quarantineTable: Record<string, unknown>[],
  context: {
    headers: string[];
    rows: Record<string, unknown>[];
    savedMappings: KnownMapping[];
    /** When grid headers are target field IDs, pass schema fields so DQX contract names map to columns. */
    schemaFields?: SchemaField[];
    /** When set (including empty Map), join quarantine rule names to contract metadata for scope + columns. */
    contractRuleLookup?: Map<string, DqxContractRuleMeta> | null;
  }
): DqxValidationResult {
  const { headers, rows, savedMappings, schemaFields, contractRuleLookup } = context;
  const lookups = buildMappingLookups(savedMappings);
  const contractNameToTargetId = schemaFields?.length
    ? buildContractFieldNameToTargetId(schemaFields)
    : undefined;

  const cellErrors = new Map<string, DqxCellError>();
  const errorReport: DqxErrorReportRow[] = [];

  quarantineTable.forEach((rawRow, quarantineTableIndex) => {
    const row = rawRow;
    const rowIndex = resolveQuarantineWorksheetRowIndex(row, rows.length, quarantineTableIndex);
    if (rowIndex < 0) return;

    const inputFallback = aliasInputDataFieldIdsToFieldNames(
      parseInputData(getQuarantineRowField(row, "input_data", "INPUT_DATA", "inputData")),
      schemaFields
    );

    const errors = parseQuarantineRulesArray(
      getQuarantineRowField(row, "errors", "ERRORS", "error")
    );
    const warnings = parseQuarantineRulesArray(
      getQuarantineRowField(row, "warnings", "WARNINGS", "warning")
    );
    const combined: { rule: QuarantineRuleRow; kind: "error" | "warning" }[] = [
      ...errors.map((rule) => ({ rule, kind: "error" as const })),
      ...warnings.map((rule) => ({ rule, kind: "warning" as const })),
    ];

    for (const { rule, kind } of combined) {
      const label = kind === "warning" ? `Warning: ${ruleLabel(rule)}` : ruleLabel(rule);
      const nm = ruleName(rule);
      const meta =
        contractRuleLookup && nm ? lookupContractRuleMeta(contractRuleLookup, nm) : undefined;
      let columnNames = columnNamesForRule(rule);
      if (columnNames.length === 0 && meta?.affected_columns?.length) {
        columnNames = meta.affected_columns.map((c) => String(c));
      }

      const multi = ruleIsMultiCellViolation(rule, meta, columnNames.length);
      const violationScope: DqxCellError["violationScope"] = multi ? "multi" : "single";

      const bucket = mapContractRuleTypeToBucket(meta?.rule_type, {
        ruleName: nm,
        checkFunction: meta?.check_function ?? rule.function,
      });

      if (columnNames.length === 0) {
        const detail =
          buildCrossFieldDetail(rule, inputFallback, meta) ??
          (meta?.check_function === "is_in_list" ? inListDetailFromMeta(meta) : undefined);
        const row: DqxErrorReportRow = {
          columnName: "",
          columnIndex: -1,
          rowIndex: rowIndex + 1,
          value: "",
          error: label,
          ruleType: meta?.rule_type,
          bucket,
        };
        if (detail) row.detail = detail;
        errorReport.push(row);
        continue;
      }

      for (const colName of columnNames) {
        const colIndex = resolveColumnIndex(
          String(colName),
          headers,
          lookups,
          contractNameToTargetId,
          schemaFields
        );
        if (colIndex < 0) {
          const detail =
            buildCrossFieldDetail(rule, inputFallback, meta) ??
            (meta?.check_function === "is_in_list" ? inListDetailFromMeta(meta) : undefined);
          const er: DqxErrorReportRow = {
            columnName: String(colName),
            columnIndex: -1,
            rowIndex: rowIndex + 1,
            value: "",
            error: label,
            ruleType: meta?.rule_type,
            bucket,
          };
          if (detail) er.detail = detail;
          errorReport.push(er);
          continue;
        }

        const headerKey = headers[colIndex] ?? String(colName);
        const targetColumnName =
          schemaFields?.find((sf) => sf.id === headerKey)?.fieldName ?? headerKey;
        const value = cellValue(rows, rowIndex, headerKey, inputFallback);
        const key = `${rowIndex}-${colIndex}`;

        const existing = cellErrors.get(key);
        const fn = typeof rule.function === "string" ? rule.function.trim() : "";
        if (existing) {
          if (!existing.rules.includes(label)) existing.rules.push(label);
          if (nm && !existing.ruleNames.includes(nm)) existing.ruleNames.push(nm);
          if (fn) {
            if (!existing.ruleFunctions) existing.ruleFunctions = [];
            if (!existing.ruleFunctions.includes(fn)) existing.ruleFunctions.push(fn);
          }
          existing.violationScope =
            existing.violationScope === "multi" || violationScope === "multi" ? "multi" : "single";
        } else {
          cellErrors.set(key, {
            rowIndex,
            columnIndex: colIndex,
            sourceColumnName: headerKey,
            targetColumnName,
            value,
            rules: [label],
            ruleNames: nm ? [nm] : [],
            ruleFunctions: fn ? [fn] : [],
            violationScope,
            category: PLACEHOLDER_CATEGORY,
          });
        }

        {
          const detail =
            buildCrossFieldDetail(rule, inputFallback, meta) ??
            (meta?.check_function === "is_in_list" ? inListDetailFromMeta(meta) : undefined);
          const er: DqxErrorReportRow = {
            columnName: targetColumnName,
            columnIndex: colIndex,
            rowIndex: rowIndex + 1,
            value,
            error: label,
            ruleType: meta?.rule_type,
            bucket,
          };
          if (detail) er.detail = detail;
          errorReport.push(er);
        }
      }
    }
  });

  const totalErrorCells =
    cellErrors.size > 0 ? cellErrors.size : errorReport.length > 0 ? errorReport.length : 0;

  return {
    hasErrors: cellErrors.size > 0 || errorReport.length > 0,
    totalErrorCells,
    summary: { ...EMPTY_SUMMARY },
    cellErrors,
    errorReport,
  };
}
